// filepath: resources/js/components/pages/CMSPages/FriVia/Results/QuizResultsPage.jsx
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../basic/index.jsx';
import { Spinner, CollapseBlock } from '../../../../common/index.jsx';
import { DownloadOutlined } from '@ant-design/icons';
import QuizResultsTable from './QuizResultsTable.jsx';
import QuizQuestionAnswersChart from './QuizQuestionAnswersChart.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import { useGetQuizQuery } from '../../../../../redux/services/quizzes.jsx';
import { useGetAggregatedStudentQuizCorrectAnswersQuery } from '../../../../../redux/services/quiz_student_answers.jsx';
import { useGetQuizQuestionQuery } from '../../../../../redux/services/quiz_questions.jsx';
import PropTypes from 'prop-types';

const QuizResultsPage = ({ mode }) => {
  const { id } = useParams();
  const isQuestionMode = mode === 'question';
  const entityId = id ? Number(id) : null;
  const quizId = !isQuestionMode ? entityId : null;
  const questionId = isQuestionMode ? entityId : null;
  const { t } = useTranslation();

  useEffect(() => {
    document.title = isQuestionMode
      ? t('Výsledky otázky') + ' - FRI CMS'
      : t('Výsledky kvízu') + ' - FRI CMS';
  }, [t, isQuestionMode]);

  const { data: quizDetail, isLoading: isQuizLoading } = useGetQuizQuery(
    quizId,
    { skip: !quizId },
  );
  const { data: questionDetail, isLoading: isQuestionLoading } =
    useGetQuizQuestionQuery(questionId, { skip: !questionId });
  const { data: aggregatedData, isLoading: isResultsLoading } =
    useGetAggregatedStudentQuizCorrectAnswersQuery(
      isQuestionMode ? { fv_question_id: questionId } : { fv_quiz_id: quizId },
      { skip: !entityId },
    );

  const rows = Array.isArray(aggregatedData)
    ? aggregatedData
    : Array.isArray(aggregatedData?.data)
      ? aggregatedData.data
      : [];
  const activeRows = rows.filter((row) => Number(row?.student_active ?? 1) === 1);
  const isLoading =
    isResultsLoading || (isQuestionMode ? isQuestionLoading : isQuizLoading);

  const handleExportCSV = () => {
    if (!activeRows || activeRows.length === 0) return;

    if (isQuestionMode) {
      const headers = [
        'Priezvisko',
        'Meno',
        'Študijná skupina',
        'Osobné číslo',
        'IP adresa',
        'Odpoveď študenta',
        'Odpovedal správne',
      ];
      const csvContent = [
        headers.join(';'),
        ...activeRows.map((row) => {
          const answered = (row.answered_correctly || '').toString();
          const localized =
            answered === 'Yes' ? 'Áno' : answered === 'No' ? 'Nie' : answered;
          return [
            row.last_name || '',
            row.first_name || '',
            row.study_group || '',
            row.uidnumber || '',
            row.ip_address || '',
            row.student_answers_text || '',
            localized,
          ]
            .map((field) => `"${String(field).replace(/"/g, '""')}"`)
            .join(';');
        }),
      ].join('\n');

      const bom = '\uFEFF';
      const blob = new Blob([bom + csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vysledky-otazky-${questionId || 'unknown'}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      return;
    }

    const headers = [
      'Priezvisko',
      'Meno',
      'Študijná skupina',
      'Osobné číslo',
      'Počet správnych odpovedí',
    ];
    const csvContent = [
      headers.join(';'),
      ...activeRows.map((row) =>
        [
          row.last_name || '',
          row.first_name || '',
          row.study_group || '',
          row.uidnumber || '',
          row.correct_count || 0,
        ]
          .map((field) => `"${String(field).replace(/"/g, '""')}"`)
          .join(';'),
      ),
    ].join('\n');

    // Add UTF-8 BOM at the beginning
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vysledky-kvizu-${quizId || 'unknown'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <AppBarCMS
        title={
          isQuestionMode
            ? `${t('Výsledky otázky')}${questionDetail?.text ? `:  ${questionDetail.text}` : ''}`
            : `${t('Výsledky kvízu')}${quizId && quizDetail?.title ? `: ${quizDetail.title}` : ''}`
        }
        backUrl={routes.cmsQuizzes}
        backUrlTooltip={t('Kvízy')}
        permissionsBasic={isQuestionMode ? 'quiz-questions' : 'quizzes'}
        mainActions={
          !isLoading && activeRows.length > 0
            ? [
                {
                  icon: <DownloadOutlined />,
                  title: isQuestionMode
                    ? t('Exportovať výsledky otázky do CSV')
                    : t('Exportovať výsledky kvízu do CSV'),
                  onClick: handleExportCSV,
                  permission: 'quiz-student-answers',
                },
              ]
            : undefined
        }
      />
      <div style={{ margin: '10px' }}>
        {isLoading && <Spinner />}
        {!isLoading && entityId && (
          <CollapseBlock collapsible={true}>
            <QuizResultsTable
              quizId={quizId || undefined}
              questionId={questionId || undefined}
              isQuestionMode={isQuestionMode}
              rows={activeRows}
            />
          </CollapseBlock>
        )}
        {!isLoading && isQuestionMode && activeRows.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <QuizQuestionAnswersChart rows={activeRows} />
          </div>
        )}
      </div>
    </>
  );
};

QuizResultsPage.propTypes = {
  mode: PropTypes.oneOf(['quiz', 'question']),
};

export default QuizResultsPage;
